import { media } from "@/styles/media";
import { IProductType } from "@/typings/Props";
import { useEffect, useState } from "react";
import styled from "styled-components";

export default function YouTube(product: IProductType) {
  const [isVideoAvailable, setIsVideoAvailable] = useState(true);

  // YouTube URL에서 비디오 ID 추출 (다양한 형식 지원)
  const getVideoId = (url: string) => {
    // youtu.be 형식: https://youtu.be/VIDEO_ID?si=...
    const youtuBeMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (youtuBeMatch) {
      return youtuBeMatch[1];
    }

    // youtube.com/embed 형식: https://www.youtube.com/embed/VIDEO_ID?si=...
    const embedMatch = url.match(/youtube\.com\/embed\/([^?&]+)/);
    if (embedMatch) {
      return embedMatch[1];
    }

    // youtube.com/watch 형식: https://www.youtube.com/watch?v=VIDEO_ID
    const watchMatch = url.match(/youtube\.com\/watch\?v=([^&]+)/);
    if (watchMatch) {
      return watchMatch[1];
    }

    return null;
  };

  // 비디오 ID를 embed URL로 변환
  const getEmbedUrl = (url: string) => {
    const videoId = getVideoId(url);
    if (!videoId) return url;

    // URL에서 시간 파라미터(si) 추출
    const siMatch = url.match(/[?&]si=([^&]+)/);
    const siParam = siMatch ? `&si=${siMatch[1]}` : "";

    return `https://www.youtube.com/embed/${videoId}?${siParam}`;
  };

  // YouTube oEmbed API를 사용하여 비디오 유효성 검사
  const checkVideoAvailability = async (videoId: string) => {
    try {
      const response = await fetch(
        `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${videoId}&format=json`
      );
      return response.ok;
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  useEffect(() => {
    const videoId = getVideoId(product.youtube);
    if (videoId) {
      checkVideoAvailability(videoId).then(setIsVideoAvailable);
    }
  }, [product.youtube]);

  // 비디오가 사용할 수 없으면 컴포넌트를 렌더링하지 않음
  if (!isVideoAvailable) {
    return null;
  }

  return (
    <YoutubeWrapper>
      <Player>
        <Iframe
          width="100%"
          height="100%"
          src={getEmbedUrl(product.youtube)}
          title="YouTube video player"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        />
      </Player>
    </YoutubeWrapper>
  );
}

const YoutubeWrapper = styled.div`
  padding: 40px 0;
  margin: 0 auto;
  max-width: 1245px;
  width: 100%;

  ${media.tablet`
  padding: 150px 5% 20px 5%;
  `}
`;

const Player = styled.div`
  position: relative;
  padding-top: 56.25%;
`;

const Iframe = styled.iframe`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;
